import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { FaLine, FaLinkedin } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

// Gmail Icon Component (Official Google Colors)
const GmailIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
    <path
      d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"
      fill="#EA4335"
    />
  </svg>
);

interface ContactDialogProps {
  open: boolean;
  onClose: () => void;
}

const ANIMATION_DURATION = '0.3s';

export default function ContactDialog({ open, onClose }: ContactDialogProps) {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = async () => {
    await navigator.clipboard.writeText('aa691109@gmail.com');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const contactItems = [
    {
      icon: <FaLine />,
      label: 'LINE',
      value: 'jerry_yan.wang',
      href: 'https://line.me/ti/p/~jerry_yan.wang',
      gradient: 'linear-gradient(135deg, #00b900 0%, #00e000 100%)',
      shadowColor: 'rgba(0, 185, 0, 0.4)',
      hoverShadow: 'rgba(0, 185, 0, 0.5)',
    },
    {
      icon: <FaLinkedin />,
      label: 'LinkedIn',
      value: 'Yen-Kun Wang',
      href: 'https://www.linkedin.com/in/yen-kun-wang-29bbb4b6/',
      gradient: 'linear-gradient(135deg, #0077b5 0%, #00a0dc 100%)',
      shadowColor: 'rgba(0, 119, 181, 0.4)',
      hoverShadow: 'rgba(0, 119, 181, 0.5)',
    },
    {
      icon: <GmailIcon />,
      label: 'Email',
      value: 'aa691109@gmail.com',
      onClick: handleCopyEmail,
      gradient: 'white',
      shadowColor: 'rgba(234, 67, 53, 0.3)',
      hoverShadow: 'rgba(234, 67, 53, 0.4)',
      isGmail: true,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          overflow: 'visible',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: 'white',
          textAlign: 'center',
          pt: 5,
          pb: 2,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: 4,
            fontSize: '0.9rem',
            opacity: 0.9,
            display: 'block',
            mb: 1,
          }}
        >
          {t('contact.sectionLabel')}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t('contact.sectionTitle')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 5 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            textAlign: 'center',
            mb: 1,
          }}
        >
          {t('contact.sectionDescription')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            textAlign: 'center',
            mb: 4,
          }}
        >
          aa691109@gmail.com
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 4,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {contactItems.map((item) => (
            <Box key={item.label} sx={{ textAlign: 'center' }}>
              <Tooltip
                title={
                  item.isGmail ? (copied ? t('contact.copied') : t('contact.clickToCopy')) : ''
                }
                arrow
                placement="top"
              >
                <IconButton
                  component={item.href ? 'a' : 'button'}
                  href={item.href}
                  target={item.href ? '_blank' : undefined}
                  rel={item.href ? 'noopener noreferrer' : undefined}
                  onClick={item.onClick}
                  sx={{
                    width: 80,
                    height: 80,
                    background: item.gradient,
                    color: item.isGmail ? undefined : 'white',
                    fontSize: item.isGmail ? '2.8rem' : '2.2rem',
                    boxShadow: `0 0 25px ${item.shadowColor}`,
                    '&:hover': {
                      transform: 'translateY(-6px) scale(1.1)',
                      boxShadow: `0 15px 30px ${item.hoverShadow}`,
                    },
                    transition: `all ${ANIMATION_DURATION} ease`,
                  }}
                >
                  {item.icon}
                </IconButton>
              </Tooltip>
              <Typography
                variant="body2"
                sx={{
                  display: 'block',
                  color: 'rgba(255, 255, 255, 0.9)',
                  mt: 1.5,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                }}
              >
                {item.isGmail && copied ? t('contact.copied') : item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
